<script lang="ts" setup>
import type { NuxtError } from '#app'
import { useAutobidAuth } from '@autobid/nuxt-auth/src/composables/useAutobidAuth'
import { useAutomaticallyRefreshAuction } from '@autobid/ui/composables/useAutomaticallyRefreshAuction'
import { useBody } from '@autobid/ui/composables/useBody'
import { useMainDomainLink } from '@autobid/ui/composables/useMainDomainLink'
import { onMounted } from 'vue'

interface Props {
  error: NuxtError
}

const props = defineProps<Props>()

const { isAuthed, setPageRedirectData } = useAutobidAuth()

const errorData
  = typeof props.error.data === 'string'
    ? JSON.parse(props.error?.data ?? '{}')
    : props.error.data ?? {}

useBody()
useAutomaticallyRefreshAuction()

onMounted(() => {
  const ACCESS_ERROR_CODES = [403, 401]
  const notRedirectable
    = props.error.statusCode === 404
      || (isAuthed.value && ACCESS_ERROR_CODES.includes(props.error.statusCode))

  setPageRedirectData({ redirectable: !notRedirectable })
})

const { validate } = useMainDomainLink()

function handleErrorLinkClick(e: MouseEvent) {
  if (e.target instanceof HTMLElement) {
    const link = e.target.closest('a')
    if (!link)
      return
    if (!validate(link.href))
      return

    e.preventDefault()

    window.location.href = link.href
  }
}
onMounted(() => {
  window.addEventListener('click', handleErrorLinkClick)
})
onUnmounted(() => {
  window.removeEventListener('click', handleErrorLinkClick)
})
</script>

<template>
  <client-only>
    <LazyCommonDialogNotSupportedBrowser is-error />
  </client-only>

  <div
    v-if="errorData.hideLayout"
    class="container mx-auto flex min-h-screen items-center justify-center pb-20"
  >
    <LazyCommonStaticErrorPage :error="error" />
  </div>
  <div v-else>
    <NuxtLayout>
      <div class="flex-1 pb-8">
        <autobid-page
          :slug="error.statusCode.toString()"
          :allow-empty="true"
          ignore-blacklist
        >
          <template #default="{ pageData }">
            <LazyCommonStrapiPage v-if="pageData?.page" :page-data="pageData" />
            <LazyCommonStaticErrorPage v-else :error="error" />
          </template>
        </autobid-page>
      </div>
    </NuxtLayout>

    <LazyCommonStrapiPopup />
    <LazyCommonDialogRequestError />
    <LazyCommonExternalLinkDialog />
  </div>
</template>
